// src/utils/exifReader.ts - Modern EXIF reader using exifr library

import * as exifr from 'exifr';
import { ImageFile } from '../types';

export interface ImageOrientation {
  width: number;
  height: number;
  isLandscape: boolean;
  exifOrientation: number;
  rotationNeeded: number; // degrees
  exifData: ExifData;     // full parsed EXIF for caching
}

export interface ExifData {
  orientation: number;
  focalLength35mm: number | null;
  focalLength: number | null;
  width: number | null;
  height: number | null;
  make: string | null;
  model: string | null;
  dateTime: string | null;
}

// Enhanced logging utility
const logger = {
  info: (message: string, data?: any) => {
    console.log(`[ExifReader] ${message}`, data || '');
  },
  warn: (message: string, data?: any) => {
    console.warn(`[ExifReader] ${message}`, data || '');
  },
  error: (message: string, error?: any) => {
    console.error(`[ExifReader] ${message}`, error || '');
  }
};

/**
 * Convert EXIF orientation value to rotation degrees
 */
const getRotationDegrees = (orientation: number): number => {
  switch (orientation) {
    case 1: return 0;    // Normal
    case 2: return 0;    // Flip horizontal (no rotation)
    case 3: return 180;  // Rotate 180°
    case 4: return 180;  // Flip vertical
    case 5: return 270;  // Transpose
    case 6: return 90;   // Rotate 90° clockwise (iPhone landscape)
    case 7: return 90;   // Transverse
    case 8: return 270;  // Rotate 270° clockwise
    default: return 0;
  }
};

/**
 * Get raw image dimensions without EXIF rotation
 */
const getRawDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      const dimensions = {
        width: img.naturalWidth,
        height: img.naturalHeight
      };
      URL.revokeObjectURL(url);
      resolve(dimensions);
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Failed to load image dimensions: ${file.name}`));
    };
    
    img.src = url;
  });
};

/**
 * Read EXIF data from file using modern exifr library
 */
export const readExifData = async (file: File): Promise<ExifData> => {
  try {
    logger.info(`Reading EXIF data from ${file.name}`);
    
    // Configure exifr for optimal performance and compatibility
    const exifOptions = {
      // Only extract what we need for better performance
      tiff: true,
      exif: true,
      gps: false,
      xmp: false,
      iptc: false,
      icc: false,
      jfif: false,
      
      // Pick specific tags for even better performance
      pick: [
        'Orientation',
        'Make', 
        'Model',
        'DateTime',
        'DateTimeOriginal',
        'FocalLength',
        'FocalLengthIn35mmFilm',
        'FocalLengthIn35mmFormat', // Alternative field name
        'FocalLengthIn35mmEquiv',  // Another possible variant
        'ExifImageWidth',
        'ExifImageHeight',
        'ImageWidth',
        'ImageHeight'
      ],
      
      // Enable translation for human-readable values
      translateKeys: true,
      translateValues: false, // We want raw numeric values for focal length
      reviveValues: true,
      
      // Error handling
      mergeOutput: true
    };

    // Read EXIF data using exifr
    const exifResult = await exifr.parse(file, exifOptions);
    
    if (!exifResult) {
      logger.info(`No EXIF data found in ${file.name}`);
      return getDefaultExifData();
    }

    // Extract and normalize the data we need
    // Try multiple field names for 35mm equivalent focal length
    const focalLength35mm = exifResult.FocalLengthIn35mmFilm || 
                           exifResult.FocalLengthIn35mmFormat || 
                           exifResult.FocalLengthIn35mmEquiv || 
                           null;
    
    const exifData: ExifData = {
      orientation: exifResult.Orientation || 1,
      focalLength35mm: focalLength35mm,
      focalLength: exifResult.FocalLength || null,
      width: exifResult.ExifImageWidth || exifResult.ImageWidth || null,
      height: exifResult.ExifImageHeight || exifResult.ImageHeight || null,
      make: exifResult.Make ? String(exifResult.Make).trim() : null,
      model: exifResult.Model ? String(exifResult.Model).trim() : null,
      dateTime: exifResult.DateTimeOriginal || exifResult.DateTime || null
    };

    // Log successful extraction
    logger.info(`EXIF data extracted successfully from ${file.name}`, {
      focalLength: exifData.focalLength,
      focalLength35mm: exifData.focalLength35mm,
      make: exifData.make,
      model: exifData.model,
      dimensions: exifData.width && exifData.height ? `${exifData.width}x${exifData.height}` : 'N/A',
      orientation: exifData.orientation
    });

    return exifData;

  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown parsing error';
    logger.error(`Failed to read EXIF data from ${file.name}`, errorMessage);
    
    // Return defaults instead of throwing to maintain backward compatibility
    return getDefaultExifData();
  }
};

/**
 * Get default EXIF data when extraction fails
 */
const getDefaultExifData = (): ExifData => {
  return {
    orientation: 1,
    focalLength35mm: null,
    focalLength: null,
    width: null,
    height: null,
    make: null,
    model: null,
    dateTime: null
  };
};

/**
 * Get actual image orientation considering EXIF rotation
 */
export const getActualOrientation = async (file: File): Promise<ImageOrientation> => {
  try {
    // Get both raw dimensions and EXIF orientation
    const [dimensions, exifData] = await Promise.all([
      getRawDimensions(file),
      readExifData(file)
    ]);
    
    const exifOrientation = exifData.orientation;
    
    // Determine if rotation affects dimensions
    const needsRotation = [5, 6, 7, 8].includes(exifOrientation);
    
    // Calculate actual display dimensions
    const actualWidth = needsRotation ? dimensions.height : dimensions.width;
    const actualHeight = needsRotation ? dimensions.width : dimensions.height;
    
    return {
      width: actualWidth,
      height: actualHeight,
      isLandscape: actualWidth > actualHeight,
      exifOrientation: exifOrientation,
      rotationNeeded: getRotationDegrees(exifOrientation),
      exifData
    };
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logger.error(`Failed to determine image orientation for ${file.name}`, errorMessage);
    
    // Fallback to just raw dimensions
    try {
      const dimensions = await getRawDimensions(file);
      return {
        width: dimensions.width,
        height: dimensions.height,
        isLandscape: dimensions.width > dimensions.height,
        exifOrientation: 1,
        rotationNeeded: 0
      };
    } catch (fallbackError) {
      throw new Error(`Failed to determine image orientation: ${errorMessage}`);
    }
  }
};

/**
 * Quick check if file is likely to have EXIF data
 */
export const hasExifData = async (file: File): Promise<boolean> => {
  try {
    // exifr is very fast for this check
    const hasExif = await exifr.parse(file, { 
      tiff: true, 
      exif: false, 
      pick: ['Orientation']
    });
    return !!hasExif;
  } catch {
    return false;
  }
};

/**
 * Read EXIF orientation specifically (for backward compatibility)
 */
export const readExifOrientation = async (file: File): Promise<number> => {
  try {
    const orientation = await exifr.orientation(file);
    return orientation || 1;
  } catch {
    return 1;
  }
};

/**
 * Check if any images in a dataset have EXIF data
 */
export const hasAnyExifData = async (images: ImageFile[]): Promise<boolean> => {
  try {
    logger.info(`Checking EXIF presence across ${images.length} images`);
    
    // Check just the first few images for performance - if they don't have EXIF, 
    // likely the whole dataset doesn't (common in datasets from certain sources)
    const samplesToCheck = Math.min(images.length, 3);
    const samples = images.slice(0, samplesToCheck);
    
    for (const image of samples) {
      const hasExif = await hasExifData(image.file);
      if (hasExif) {
        logger.info(`Found EXIF data in dataset (checked ${image.file.name})`);
        return true;
      }
    }
    
    logger.info(`No EXIF data found in dataset (checked ${samplesToCheck} samples)`);
    return false;
  } catch (error) {
    logger.error('Failed to check EXIF presence in dataset', error);
    // Conservative fallback - assume EXIF exists to maintain normal validation
    return true;
  }
};